import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
	CustomIconButton,
	CustomTooltip,
	icon,
	IconComponent,
	VerticalDivider,
} from "../../../../../../../../common/components";
import DropDownAction from "./DropDownActions";
import { translate } from "../../../../../../../../common/providers";
import { useTrackEvent } from "../../../../../../../../common/hooks";
import { SavingIcon } from "../header/components";
import styles from "./RichtextEditor.module.css";

const EditorActions = ({ editor, editorState }) => {
	const trackEvent = useTrackEvent();
	const noteId = useSelector(({ note }) => note.selectedNote?.id);
	const HEADING_OPTIONS = useMemo(
		() => [
			{ value: 0, label: translate("notes.right-panel.editor-actions.normal") },
			{ value: 1, label: translate("notes.right-panel.editor-actions.heading1") },
			{ value: 2, label: translate("notes.right-panel.editor-actions.heading2") },
			{ value: 3, label: translate("notes.right-panel.editor-actions.heading3") },
		],
		[]
	);
	const LIST_OPTIONS = useMemo(
		() => [
			{
				value: "bullet",
				label: (
					<CustomTooltip placement="left" title={translate("notes.right-panel.editor-actions.bullet-list")}>
						<IconComponent color="var(--grey-700)" icon={icon.faList} size="xs" />
					</CustomTooltip>
				),
			},
			{
				value: "ordered",
				label: (
					<CustomTooltip placement="left" title={translate("notes.right-panel.editor-actions.ordered-list")}>
						<IconComponent color="var(--grey-700)" icon={icon.faList12} size="xs" />
					</CustomTooltip>
				),
			},
		],
		[]
	);
	const handleChangeHeading = (level) => {
		if (level === 0) {
			editor.chain().focus().setParagraph().run();
		} else {
			editor.chain().focus().toggleHeading({ level }).run();
		}
	};
	const handleChangeListMode = (mode) => {
		if (mode === "bullet") {
			editor.chain().focus().toggleBulletList().run();
		} else {
			editor.chain().focus().toggleOrderedList().run();
		}
	};
	const handleCancelListMode = () => {
		editor.chain().focus().liftListItem("listItem").run();
	};
	const handleUndo = () => {
		editor.chain().focus().undo().run();
		trackEvent("Notes_Undo");
	};
	const handleRedo = () => {
		editor.chain().focus().redo().run();
		trackEvent("Notes_Redo");
	};
	return (
		<div className={styles.editorActions__container}>
			{noteId && (
				<>
					<div>
						<DropDownAction
							disabled={!editor.isEditable}
							items={HEADING_OPTIONS}
							value={
								(editorState.isHeading?.level && (
									<>
										{translate(
											`notes.right-panel.editor-actions.heading${
												editorState.isHeading?.level || "1"
											}`
										)}
									</>
								)) ||
								translate("notes.right-panel.editor-actions.normal")
							}
							onSelect={handleChangeHeading}
						/>
						<CustomIconButton
							className={styles.actionButton}
							color={editorState.isBold ? "primary" : "secondary"}
							disabled={!editorState.canBold || !editor.isEditable}
							icon={icon.faBold}
							size="xs"
							tooltip={translate("notes.right-panel.editor-actions.bold")}
							onClick={() => editor.chain().focus().toggleBold().run()}
						/>
						<CustomIconButton
							className={styles.actionButton}
							color={editorState.isItalic ? "primary" : "secondary"}
							disabled={!editorState.canItalic || !editor.isEditable}
							icon={icon.faItalic}
							size="xs"
							tooltip={translate("notes.right-panel.editor-actions.italic")}
							onClick={() => editor.chain().focus().toggleItalic().run()}
						/>
						<CustomIconButton
							className={styles.actionButton}
							color={editorState.isUnderline ? "primary" : "secondary"}
							disabled={!editorState.canUnderline || !editor.isEditable}
							icon={icon.faUnderline}
							size="xs"
							tooltip={translate("notes.right-panel.editor-actions.underline")}
							onClick={() => editor.chain().focus().toggleUnderline().run()}
						/>
						<CustomIconButton
							className={styles.actionButton}
							color={editorState.isStrike ? "primary" : "secondary"}
							disabled={!editorState.canStrike || !editor.isEditable}
							icon={icon.faStrikethrough}
							size="xs"
							tooltip={translate("notes.right-panel.editor-actions.strike-trough")}
							onClick={() => editor.chain().focus().toggleStrike().run()}
						/>
						<VerticalDivider className={styles.verticalDevider} />
						<DropDownAction
							disabled={!editor.isEditable}
							isSelected={editorState.isBulletList || editorState.isOrderedList}
							items={LIST_OPTIONS}
							tooltip={translate(
								(editorState.isOrderedList && "notes.right-panel.editor-actions.ordered-list") ||
									"notes.right-panel.editor-actions.bullet-list"
							)}
							value={
								<IconComponent
									color={
										editorState.isBulletList || editorState.isOrderedList
											? "var(--blue-500)"
											: "var(--grey-700)"
									}
									icon={(editorState.isOrderedList && icon.faList12) || icon.faList}
									size="xs"
								/>
							}
							onCancel={handleCancelListMode}
							onSelect={handleChangeListMode}
						/>
						<VerticalDivider className={styles.verticalDevider} />
					</div>
					<div className={styles.actionButtons}>
						<CustomIconButton
							className={styles.actionButton}
							color="secondary"
							disabled={!editorState.canUndo || !editor.isEditable}
							icon={icon.faUndo}
							size="xs"
							tooltip={translate("notes.right-panel.editor-actions.undo")}
							onClick={handleUndo}
						/>
						<CustomIconButton
							className={styles.actionButton}
							color="secondary"
							disabled={!editorState.canRedo || !editor.isEditable}
							icon={icon.faRedoAlt}
							size="xs"
							tooltip={translate("notes.right-panel.editor-actions.redo")}
							onClick={handleRedo}
						/>
						<SavingIcon />
					</div>
				</>
			)}
		</div>
	);
};

export default EditorActions;
